import { lazy } from 'react';

import { redirect } from 'react-router-dom';

import type { AppRouteObject } from '@/navigation/route-handle';

import OrgLayout from './_layout';

// Engine
const EngineRunnersPage = lazy(() => import('./engine/engine-runners-page'));
const EngineEventsPage = lazy(() => import('./engine/engine-events-page'));
const EvaluatorsPage = lazy(
  () => import('./engine/evaluators/evaluators-page')
);
const EvaluatorDetailPage = lazy(
  () => import('./engine/evaluators/evaluator-detail-page')
);
const EvaluatorEditPage = lazy(
  () => import('./engine/evaluators/evaluator-edit-page')
);
const AutomationsPage = lazy(
  () => import('./engine/automations/automations-page')
);
const AutomationDetailPage = lazy(
  () => import('./engine/automations/automation-detail-page')
);

const ClientsPage = lazy(() => import('./manage/clients/clients-page'));
const EnrollmentPage = lazy(
  () => import('./manage/enrollment/enrollment-page')
);
const ClientDetailPage = lazy(
  () => import('./manage/clients/client-detail-page')
);
const ConnectorEditPage = lazy(
  () => import('./manage/connectors/connector-edit-page')
);
const ConnectorFeatureCreatePage = lazy(
  () => import('./manage/connectors/connector-feature-create-page')
);
const ConnectorFeatureEditPage = lazy(
  () => import('./manage/connectors/connector-feature-edit-page')
);
const ConnectorsPage = lazy(
  () => import('./manage/connectors/connectors-page')
);
const ProviderEditPage = lazy(
  () => import('./manage/providers/provider-edit-page')
);
const ProviderFeatureCreatePage = lazy(
  () => import('./manage/providers/provider-feature-create-page')
);
const ProviderFeatureEditPage = lazy(
  () => import('./manage/providers/provider-feature-edit-page')
);
const SecretsPage = lazy(() => import('./manage/secrets/secrets-page'));
const SensitiveContentPage = lazy(
  () => import('./manage/sensitive-content/sensitive-content-page')
);
const SecretTemplateCreate = lazy(
  () => import('./manage/secrets/secret-template-create-page')
);

const OrgHomePage = lazy(() => import('./home'));
const OrgSettingsPage = lazy(() => import('./settings'));

const Role = lazy(() => import('./manage/roles/role-page'));
const RoleEdit = lazy(() => import('./manage/roles/role-edit-page'));
const Roles = lazy(() => import('./manage/roles/roles-page'));

const Teams = lazy(() => import('./manage/teams/teams-page'));
const TeamEdit = lazy(() => import('./manage/teams/team-edit-page'));
const Members = lazy(() => import('./manage/members/members-page'));
const MemberEdit = lazy(() => import('./manage/members/member-detail-page'));
const SecretDetail = lazy(() => import('./manage/secrets/secret-detail-page'));
const SecretTemplateDetail = lazy(
  () => import('./manage/secrets/secret-template-detail-page')
);
const SecretTemplateEdit = lazy(
  () => import('./manage/secrets/secret-template-edit-page')
);
const CatalogPage = lazy(() => import('./catalog/catalog-page'));
const TenantLogsPage = lazy(() => import('./tenant-logs/tenant-logs-page'));
const ConnectorCatalogEntityPage = lazy(
  () => import('./manage/connectors/connector-catalog-entity-page')
);
const SetupPage = lazy(() => import('./setup/setup-page'));
const ConfigureConnectorPage = lazy(
  () => import('./setup/configure-connector-page')
);
export const scopedRoutes: AppRouteObject[] = [
  {
    path: '/:slug',
    element: <OrgLayout />,
    children: [
      {
        index: true,
        element: <OrgHomePage />,
        handle: { deferLoading: true, skeleton: 'dashboard' },
      },
      {
        path: 'setup',
        element: <SetupPage />,
        handle: { fullWidth: true, hideBreadcrumbs: true },
      },
      {
        path: 'setup/configure/:connectorId',
        element: <ConfigureConnectorPage />,
        handle: { fullWidth: true, hideBreadcrumbs: true },
      },
      { path: '/:slug/settings', element: <OrgSettingsPage /> },
      // Roles
      {
        path: 'manage/roles',
        element: <Roles />,
        handle: { breadcrumb: 'Roles', deferLoading: true, skeleton: 'table' },
      },
      {
        path: 'manage/roles/:roleId',
        element: <Role />,
        handle: { breadcrumb: 'Role' },
      },
      {
        path: 'manage/roles/:roleId/settings',
        element: <RoleEdit />,
        handle: { breadcrumb: 'Role Settings' },
      },
      {
        path: 'manage/roles/:roleId/edit',
        loader: ({ params }) =>
          redirect(`/${params.slug}/manage/roles/${params.roleId}/settings`),
      },

      // Teams
      {
        path: 'manage/teams',
        element: <Teams />,
        handle: { breadcrumb: 'Teams', deferLoading: true, skeleton: 'table' },
      },
      {
        path: 'manage/teams/:teamId/settings',
        element: <TeamEdit />,
        handle: { breadcrumb: 'Team Settings' },
      },
      {
        path: 'manage/teams/:teamId/settings/members-section',
        element: <TeamEdit />,
        handle: { breadcrumb: 'Team Members', isTab: true },
      },
      {
        path: 'manage/teams/:teamId/settings/roles-section',
        element: <TeamEdit />,
        handle: { breadcrumb: 'Team Roles', isTab: true },
      },
      {
        path: 'manage/teams/:teamId/edit',
        loader: ({ params }) =>
          redirect(`/${params.slug}/manage/teams/${params.teamId}/settings`),
      },
      {
        path: 'manage/teams/:teamId/edit/members-section',
        loader: ({ params }) =>
          redirect(
            `/${params.slug}/manage/teams/${params.teamId}/settings/members-section`
          ),
      },
      {
        path: 'manage/teams/:teamId/edit/roles-section',
        loader: ({ params }) =>
          redirect(
            `/${params.slug}/manage/teams/${params.teamId}/settings/roles-section`
          ),
      },

      // Members & Invitations
      {
        path: 'manage/members',
        element: <Members />,
        handle: {
          breadcrumb: 'Members',
          deferLoading: true,
          skeleton: 'tabbed-table',
        },
      },
      {
        path: 'manage/members/members-list',
        loader: ({ params }) => redirect(`/${params.slug}/manage/members`),
      },
      {
        path: 'manage/members/member-invitations',
        element: <Members />,
        handle: {
          breadcrumb: 'Members',
          deferLoading: true,
          skeleton: 'tabbed-table',
          isTab: true,
        },
      },
      {
        path: 'manage/members/:userId/configure',
        element: <MemberEdit />,
        handle: { breadcrumb: 'Configure Member' },
      },
      {
        path: 'manage/members/:userId/edit',
        loader: ({ params }) =>
          redirect(`/${params.slug}/manage/members/${params.userId}/configure`),
      },

      // Connectors
      {
        path: 'manage/connectors',
        element: <ConnectorsPage />,
        handle: {
          breadcrumb: 'Connectors',
          deferLoading: true,
          skeleton: 'tabbed-cards',
        },
      },
      {
        path: 'manage/connectors/connectors-list',
        loader: ({ params }) => redirect(`/${params.slug}/manage/connectors`),
      },
      {
        path: 'manage/connectors/providers',
        element: <ConnectorsPage />,
        handle: {
          breadcrumb: 'Providers',
          deferLoading: true,
          skeleton: 'tabbed-cards',
          isTab: true,
        },
      },
      {
        path: 'manage/connectors/:connectorId/view/details',
        loader: ({ params }) =>
          redirect(
            `/${params.slug}/manage/connectors/${params.connectorId}/view`
          ),
      },
      {
        path: 'manage/connectors/:connectorId/view/:tabId?',
        element: <ConnectorEditPage />,
        handle: { breadcrumb: 'Connector View', tabParam: 'tabId' },
      },
      {
        path: 'manage/connectors/:connectorId/edit/:tabId?',
        loader: ({ params }) =>
          redirect(
            `/${params.slug}/manage/connectors/${params.connectorId}/view${
              params.tabId ? `/${params.tabId}` : ''
            }`
          ),
      },
      {
        path: 'manage/connectors/:connectorId/features/create',
        element: <ConnectorFeatureCreatePage />,
        handle: { breadcrumb: 'Add Connector Feature' },
      },
      {
        path: 'manage/connectors/:connectorId/features/:featureId/view',
        element: <ConnectorFeatureEditPage />,
        handle: { breadcrumb: 'Feature View' },
      },
      {
        path: 'manage/connectors/:connectorId/features/:featureId/edit',
        loader: ({ params }) =>
          redirect(
            `/${params.slug}/manage/connectors/${params.connectorId}/features/${params.featureId}/view`
          ),
      },
      {
        path: 'manage/connectors/:connectorId/entities/:entitySlug/:tabId?',
        element: <ConnectorCatalogEntityPage />,
        handle: { breadcrumb: 'Catalog', tabParam: 'tabId' },
      },

      // Providers
      {
        path: 'manage/providers',
        loader: ({ params }) =>
          redirect(`/${params.slug}/manage/connectors/providers`),
      },
      {
        path: 'manage/providers/:providerId/settings',
        element: <ProviderEditPage />,
        handle: { breadcrumb: 'Provider Settings' },
      },
      {
        path: 'manage/providers/:providerId/edit',
        loader: ({ params }) =>
          redirect(
            `/${params.slug}/manage/providers/${params.providerId}/settings`
          ),
      },
      {
        path: 'manage/providers/:providerId/features/create',
        element: <ProviderFeatureCreatePage />,
        handle: { breadcrumb: 'Create Feature' },
      },
      {
        path: 'manage/providers/:providerId/features/:featureId/configure',
        element: <ProviderFeatureEditPage />,
        handle: { breadcrumb: 'Configure Provider Feature' },
      },
      {
        path: 'manage/providers/:providerId/features/:featureId/edit',
        loader: ({ params }) =>
          redirect(
            `/${params.slug}/manage/providers/${params.providerId}/features/${params.featureId}/configure`
          ),
      },

      // Secrets
      {
        path: 'manage/secrets',
        element: <SecretsPage />,
        handle: {
          breadcrumb: 'Secrets',
          deferLoading: true,
          skeleton: 'tabbed-table',
        },
      },
      {
        path: 'manage/secrets/secrets-list',
        loader: ({ params }) => redirect(`/${params.slug}/manage/secrets`),
      },
      {
        path: 'manage/secrets/secret-templates',
        element: <SecretsPage />,
        handle: {
          breadcrumb: 'Secrets',
          deferLoading: true,
          skeleton: 'tabbed-table',
          isTab: true,
        },
      },
      {
        path: 'manage/secrets/:secretId/details',
        element: <SecretDetail />,
        handle: { breadcrumb: 'Secret Details' },
      },
      {
        path: 'manage/secrets/:secretId/edit',
        loader: ({ params }) =>
          redirect(`/${params.slug}/manage/secrets/${params.secretId}/details`),
      },
      {
        path: 'manage/secrets/templates/create',
        element: <SecretTemplateCreate />,
        handle: { breadcrumb: 'Create Template' },
      },
      {
        path: 'manage/secrets/templates/:templateId',
        element: <SecretTemplateDetail />,
        handle: { breadcrumb: 'Template' },
      },
      {
        path: 'manage/secrets/templates/:templateId/configure',
        element: <SecretTemplateEdit />,
        handle: { breadcrumb: 'Configure Template' },
      },
      {
        path: 'manage/secrets/templates/:templateId/edit',
        loader: ({ params }) =>
          redirect(
            `/${params.slug}/manage/secrets/templates/${params.templateId}/configure`
          ),
      },

      // Sensitive Content
      {
        path: 'manage/sensitive-content',
        element: <SensitiveContentPage />,
        handle: {
          breadcrumb: 'Sensitive Content',
          deferLoading: true,
          skeleton: 'table',
        },
      },

      // Clients
      {
        path: 'manage/clients',
        element: <ClientsPage />,
        handle: {
          breadcrumb: 'Clients',
          deferLoading: true,
          skeleton: 'table',
        },
      },
      // Enrollment (agent bootstrap credentials)
      {
        path: 'manage/enrollment',
        element: <EnrollmentPage />,
        handle: {
          breadcrumb: 'Enrollment',
          deferLoading: true,
          skeleton: 'table',
        },
      },
      {
        path: 'manage/clients/:clientId/details',
        element: <ClientDetailPage />,
        handle: { breadcrumb: 'Client Details' },
      },

      // Catalog
      {
        path: 'catalog',
        element: <CatalogPage />,
        handle: { breadcrumb: 'Catalog' },
      },

      // Tenant logs
      {
        path: 'logs',
        element: <TenantLogsPage />,
        handle: { breadcrumb: 'Logs' },
      },

      // Engine
      {
        path: 'engine',
        loader: ({ params }) => redirect(`/${params.slug}/engine/evaluators`),
      },
      {
        path: 'engine/config',
        loader: ({ params }) => redirect(`/${params.slug}/engine/evaluators`),
      },
      {
        path: 'engine/runners',
        element: <EngineRunnersPage />,
        handle: { breadcrumb: 'Engine Runners' },
      },
      {
        path: 'engine/events',
        element: <EngineEventsPage />,
        handle: { breadcrumb: 'Execution Events' },
      },

      // Evaluators
      {
        path: 'engine/evaluators',
        element: <EvaluatorsPage />,
        handle: {
          breadcrumb: 'Evaluators',
          deferLoading: true,
          skeleton: 'table',
        },
      },
      {
        path: 'engine/evaluators/:ruleId',
        element: <EvaluatorDetailPage />,
        handle: { breadcrumb: 'Evaluator' },
      },
      {
        path: 'engine/evaluators/:ruleId/edit',
        element: <EvaluatorEditPage />,
        handle: { breadcrumb: 'Edit Evaluator' },
      },

      // Automations
      {
        path: 'engine/automations',
        element: <AutomationsPage />,
        handle: {
          breadcrumb: 'Automations',
          deferLoading: true,
          skeleton: 'table',
        },
      },
      {
        path: 'engine/automations/:ruleId',
        element: <AutomationDetailPage />,
        handle: { breadcrumb: 'Automation' },
      },
      {
        path: 'engine/automations/:ruleId/edit',
        loader: ({ params }) =>
          redirect(`/${params.slug}/engine/automations/${params.ruleId}`),
      },
    ],
  },
];
